import React, { useEffect, useState } from "react";
import { CloudOff, CloudUpload, Check, RefreshCw } from "lucide-react";
import { subscribe, flushQueue } from "@/lib/offline";
import { toast } from "sonner";

export default function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queueSize, setQueueSize] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = async () => {
      setOnline(true);
      setSyncing(true);
      const res = await flushQueue();
      setSyncing(false);
      if (res.sent > 0) {
        toast.success(`${res.sent} alteração(ões) sincronizada(s)`);
      }
      if (res.failed > 0) {
        toast.error(`${res.failed} alteração(ões) descartada(s) (erro de servidor)`);
      }
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const unsub = subscribe((q) => setQueueSize(q.length));

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsub();
    };
  }, []);

  const manualSync = async () => {
    setSyncing(true);
    const res = await flushQueue();
    setSyncing(false);
    if (res.sent > 0) toast.success(`${res.sent} sincronizada(s)`);
    if (res.remaining > 0) toast.info(`${res.remaining} ainda pendente(s)`);
  };

  if (online && queueSize === 0) return null;

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-sm border shadow-md flex items-center gap-3 text-sm transition-all ${
        online
          ? "bg-amber-50 border-amber-300 text-amber-900"
          : "bg-slate-900 border-slate-700 text-white"
      }`}
      data-testid="offline-banner"
    >
      {online ? (
        <CloudUpload className="w-4 h-4" data-testid="offline-banner-icon-online" />
      ) : (
        <CloudOff className="w-4 h-4" data-testid="offline-banner-icon-offline" />
      )}
      <span className="font-medium" data-testid="offline-banner-text">
        {online
          ? queueSize > 0
            ? `${queueSize} alteração(ões) aguardando sincronização`
            : "Tudo sincronizado"
          : "Sem conexão — alterações ficam salvas localmente"}
      </span>
      {queueSize > 0 && online && (
        <button
          onClick={manualSync}
          disabled={syncing}
          className="ml-1 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider underline-offset-2 hover:underline disabled:opacity-50"
          data-testid="offline-banner-sync-button"
        >
          {syncing ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          Sincronizar
        </button>
      )}
    </div>
  );
}
